import React from 'react';
import { Shield, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-700 to-green-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-green-100 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </button>
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center">
              <Shield className="text-white" size={32} />
            </div>
            <div>
              <h1 className="text-4xl font-bold">Privacy Policy</h1>
              <p className="text-green-100 mt-2">Last Updated: January 16, 2026</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-lg max-w-none">
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              At Cornerstone Exim ("we," "us," or "our"), we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website{' '}
              <a href="https://www.cornerstoneexim.com" className="text-green-700 hover:text-green-800 font-semibold">
                www.cornerstoneexim.com
              </a>{' '}
              and use our agricultural export services.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-950 mb-6">1. Information We Collect</h2>
            
            <div className="bg-amber-50 rounded-xl p-6 mb-6">
              <h3 className="text-xl font-bold text-amber-950 mb-4">1.1 Personal Information</h3>
              <p className="text-gray-700 mb-4">We may collect personal information that you voluntarily provide to us, including:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Name and contact information (email address, phone number)</li>
                <li>Company name and business details</li>
                <li>Country and location information</li>
                <li>Product inquiries and preferences</li>
                <li>Communication history with our team</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-amber-950 mb-4">1.2 Automatically Collected Information</h3>
              <p className="text-gray-700 mb-4">When you visit our website, we may automatically collect:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>IP address and browser information</li>
                <li>Device type and operating system</li>
                <li>Pages visited and time spent on our website</li>
                <li>Referring website addresses</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-950 mb-6">2. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We use the collected information for the following purposes:</p>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-700 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm">✓</span>
                </div>
                <p className="text-gray-700"><strong>Business Communications:</strong> To respond to your inquiries, provide quotes, and facilitate export transactions</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-700 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm">✓</span>
                </div>
                <p className="text-gray-700"><strong>Service Improvement:</strong> To understand how our website is used and improve our services</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-700 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm">✓</span>
                </div>
                <p className="text-gray-700"><strong>Marketing:</strong> To send you information about our products and services (with your consent)</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-700 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm">✓</span>
                </div>
                <p className="text-gray-700"><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and export requirements</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-700 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm">✓</span>
                </div>
                <p className="text-gray-700"><strong>Security:</strong> To protect against fraud, unauthorized access, and security threats</p>
              </div>
            </div>
          </section>

          {/* Information Sharing */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-950 mb-6">3. Information Sharing and Disclosure</h2>
            <p className="text-gray-700 mb-6">We do not sell, rent, or trade your personal information. We may share your information only in the following circumstances:</p>
            
            <div className="bg-gray-50 rounded-xl p-6 space-y-4">
              <div>
                <h4 className="font-bold text-amber-950 mb-2">Service Providers</h4>
                <p className="text-gray-700">With trusted third-party service providers who assist us in operating our business (e.g., shipping partners, payment processors)</p>
              </div>
              <div>
                <h4 className="font-bold text-amber-950 mb-2">Legal Requirements</h4>
                <p className="text-gray-700">When required by law, court order, or government authorities</p>
              </div>
              <div>
                <h4 className="font-bold text-amber-950 mb-2">Business Transfers</h4>
                <p className="text-gray-700">In connection with a merger, acquisition, or sale of assets</p>
              </div>
              <div>
                <h4 className="font-bold text-amber-950 mb-2">With Your Consent</h4>
                <p className="text-gray-700">When you have given us explicit permission to share your information</p>
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-950 mb-6">4. Data Security</h2>
            <p className="text-gray-700 mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. These measures include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Secure socket layer (SSL) encryption for data transmission</li>
              <li>Regular security assessments and updates</li>
              <li>Restricted access to personal information</li>
              <li>Secure storage systems and databases</li>
            </ul>
            <p className="text-gray-700 mt-4">
              However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          {/* Cookies */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-950 mb-6">5. Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 mb-4">
              We use cookies and similar tracking technologies to enhance your browsing experience. Cookies are small files stored on your device that help us:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
              <li>Remember your preferences and settings</li>
              <li>Understand how you interact with our website</li>
              <li>Improve website performance and functionality</li>
              <li>Provide personalized content</li>
            </ul>
            <p className="text-gray-700">
              You can control cookies through your browser settings. However, disabling cookies may affect website functionality.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-950 mb-6">6. Your Rights and Choices</h2>
            <p className="text-gray-700 mb-4">You have the following rights regarding your personal information:</p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-bold text-amber-950 mb-2">Access</h4>
                <p className="text-gray-700 text-sm">Request access to your personal information</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-bold text-amber-950 mb-2">Correction</h4>
                <p className="text-gray-700 text-sm">Request correction of inaccurate information</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-bold text-amber-950 mb-2">Deletion</h4>
                <p className="text-gray-700 text-sm">Request deletion of your personal information</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-bold text-amber-950 mb-2">Opt-Out</h4>
                <p className="text-gray-700 text-sm">Opt-out of marketing communications</p>
              </div>
            </div>
            
            <p className="text-gray-700 mt-6">
              To exercise these rights, please contact us at{' '}
              <a href="mailto:info@cornerstoneexim.com" className="text-green-700 hover:text-green-800 font-semibold">
                info@cornerstoneexim.com
              </a>
              {' '}or{' '}
              <a href="mailto:cornerstoneeximindia@gmail.com" className="text-green-700 hover:text-green-800 font-semibold">
                cornerstoneeximindia@gmail.com
              </a>
            </p>
          </section>

          {/* International Transfers */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-950 mb-6">7. International Data Transfers</h2>
            <p className="text-gray-700">
              As we operate internationally, your information may be transferred to and processed in countries other than your own. 
              We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards to protect your information.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-950 mb-6">8. Children's Privacy</h2>
            <p className="text-gray-700">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. 
              If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>

          {/* Changes to Policy */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-950 mb-6">9. Changes to This Privacy Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. 
              The "Last Updated" date at the top of this page indicates when the policy was last revised. 
              We encourage you to review this page periodically for any updates.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-950 mb-6">10. Contact Us</h2>
            <div className="bg-gradient-to-r from-green-700 to-green-800 rounded-2xl p-8 text-white">
              <p className="text-green-50 mb-6">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="space-y-3">
                <p><strong>Cornerstone Exim</strong></p>
                <p>Email: <a href="mailto:info@cornerstoneexim.com" className="underline hover:text-green-200">info@cornerstoneexim.com</a></p>
                <p>Email: <a href="mailto:cornerstoneeximindia@gmail.com" className="underline hover:text-green-200">cornerstoneeximindia@gmail.com</a></p>
                <p>Phone: <a href="tel:+919326255288" className="underline hover:text-green-200">+91 93262 55288</a></p>
                <p>WhatsApp: <a href="https://wa.me/919326255288" target="_blank" rel="noopener noreferrer" className="underline hover:text-green-200">+91 93262 55288</a></p>
                <p>Website: <a href="https://www.cornerstoneexim.com" className="underline hover:text-green-200">www.cornerstoneexim.com</a></p>
              </div>
            </div>
          </section>

          {/* Consent */}
          <section className="bg-amber-50 rounded-2xl p-8 border-2 border-amber-200">
            <h2 className="text-2xl font-bold text-amber-950 mb-4">Your Consent</h2>
            <p className="text-gray-700">
              By using our website and services, you consent to this Privacy Policy and agree to its terms. 
              If you do not agree with this policy, please do not use our website or provide us with your personal information.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
